// src/modules/myvault/components/automations/TestFireDialog.tsx

// What? Dialog that runs a rule's `POST /rules/{id}/test` dry-run against a
//       user-picked row id and shows what would happen.
// Why?  Admins need to verify their conditions match before flipping a rule
//       on. The backend endpoint is already a dry-run (no mutation); it
//       reports whether conditions matched + the row data sample.
// How?  Row-id input + fire button. No row browser for v1 -- admins know
//       the PK because they just came from the grid.

import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import type { AutomationTestResult } from "../../hooks/useAutomationRules";

/** Map a thrown test-fire error to human copy. */
function mapTestFireError(err: unknown): string {
  // authFetch throws an Error with `.status` attached for non-OK responses.
  // 404 specifically means the row id didn't match anything in the target
  // table -- the most common user-triggered failure during dry-runs.
  const status = (err as { status?: number } | null)?.status;
  if (status === 404) {
    return (
      "Row not found in target table. " +
      "Paste a primary-key value from an existing row in the target table's grid."
    );
  }
  if (err instanceof Error) return err.message;
  return "Test run failed";
}

interface TestFireDialogProps {
  open: boolean;
  onClose: () => void;
  onRun: (rowId: string) => Promise<AutomationTestResult>;
}

const TestFireDialog: React.FC<TestFireDialogProps> = ({
  open,
  onClose,
  onRun,
}) => {
  const [rowId, setRowId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AutomationTestResult | null>(null);

  const handleRun = async () => {
    const trimmed = rowId.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const data = await onRun(trimmed);
      setResult(data);
    } catch (err) {
      setError(mapTestFireError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setRowId("");
    setError(null);
    setResult(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ width: "min(560px, 95vw)" }} role="alertdialog">
        <ModalClose />
        <Typography level="title-lg">Test this rule</Typography>
        <Typography level="body-sm" sx={{ color: "text.secondary", mb: 1 }}>
          Evaluate the rule against a real row without executing any actions.
          SEND_EMAIL actions force-redirect to the rule creator when the backend
          ships that behaviour (R-6d test-fire).
        </Typography>

        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <Input
              sx={{ flex: 1 }}
              size="sm"
              placeholder="Row primary key"
              value={rowId}
              onChange={(e) => setRowId(e.target.value)}
              disabled={busy}
            />
            <Button
              startDecorator={<PlayArrowIcon />}
              onClick={handleRun}
              loading={busy}
              disabled={!rowId.trim()}
            >
              Run test
            </Button>
          </Stack>

          {error && <Alert color="danger">{error}</Alert>}

          {result && (
            <Sheet variant="outlined" sx={{ borderRadius: "sm", p: 1.5 }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                    Would fire:
                  </Typography>
                  <Chip
                    size="sm"
                    color={result.would_fire ? "success" : "neutral"}
                    variant="soft"
                  >
                    {result.would_fire ? "yes" : "no"}
                  </Chip>
                  <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                    · Conditions:
                  </Typography>
                  <Chip
                    size="sm"
                    color={result.condition_result ? "success" : "neutral"}
                    variant="soft"
                  >
                    {result.condition_result ? "matched" : "did not match"}
                  </Chip>
                </Stack>
                <Box>
                  <Typography
                    level="body-xs"
                    sx={{ color: "text.tertiary", mb: 0.5 }}
                  >
                    Row data sample
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      fontSize: "11px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      maxHeight: 240,
                      overflow: "auto",
                    }}
                  >
                    {JSON.stringify(result.row_data_sample || {}, null, 2)}
                  </Box>
                </Box>
              </Stack>
            </Sheet>
          )}
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default TestFireDialog;
